/* Industry listing (sections/ex-industry-listing.liquid): filters, search and sort over the six cards already in the page. State is kept in the address; below 990px the panel is a drawer. */
if (!customElements.get('ex-industry-listing')) {
  customElements.define(
    'ex-industry-listing',
    class ExIndustryListing extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('.ex-industry__grid');
        this.items = Array.from(this.querySelectorAll('[data-ex-industry-item]'));
        this.checkboxes = Array.from(this.querySelectorAll('[data-ex-industry-filter]'));
        this.searchInput = this.querySelector('[data-ex-industry-search]');
        this.sortSelect = this.querySelector('[data-ex-industry-sort]');
        this.countEl = this.querySelector('[data-ex-industry-count]');
        this.emptyEl = this.querySelector('[data-ex-industry-empty]');
        if (!this.grid || !this.items.length) return;

        // The order the client put the blocks in, which "En vedette" returns to
        this.items.forEach((item, i) => {
          item.dataset.order = i;
        });

        this.setUpPanel();
        this.setUpGroups();
        this.readAddress();
        this.bind();
        this.apply({ push: false });
      }

      /* ---------- The filter panel ---------- */

      setUpPanel() {
        const side = this.querySelector('[data-ex-industry-side]');
        const button = this.querySelector('.ex-industry-filters__open');
        if (!side || !button) return;

        this.side = side;
        this.openButton = button;
        this.overlay = this.querySelector('[data-ex-industry-overlay]');
        this.closeButton = this.querySelector('[data-ex-industry-close]');
        this.desktop = window.matchMedia('(min-width: 990px)');

        side.setAttribute('data-filters-open', 'false');

        button.addEventListener('click', () => {
          if (button.getAttribute('aria-expanded') === 'true') this.closePanel();
          else this.openPanel();
        });

        if (this.closeButton) this.closeButton.addEventListener('click', () => this.closePanel());
        if (this.overlay) this.overlay.addEventListener('click', () => this.closePanel());

        this.onKeydown = (event) => {
          if (event.key === 'Escape') this.closePanel();
        };

        const onDesktop = () => {
          if (this.desktop.matches) this.closePanel();
        };
        if (this.desktop.addEventListener) this.desktop.addEventListener('change', onDesktop);
        else this.desktop.addListener(onDesktop);
      }

      openPanel() {
        if (!this.side || this.desktop.matches) return;
        this.openButton.setAttribute('aria-expanded', 'true');
        this.side.setAttribute('data-filters-open', 'true');

        this.bodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', this.onKeydown);

        // The panel is visibility:hidden until the attribute lands; flush before focusing
        if (this.closeButton) {
          const panel = this.querySelector('.ex-industry-filters');
          if (panel) void panel.offsetWidth;
          this.closeButton.focus();
        }
      }

      closePanel() {
        if (!this.side) return;
        const wasOpen = this.openButton.getAttribute('aria-expanded') === 'true';
        this.openButton.setAttribute('aria-expanded', 'false');
        this.side.setAttribute('data-filters-open', 'false');
        document.body.style.overflow = this.bodyOverflow || '';
        document.removeEventListener('keydown', this.onKeydown);

        if (wasOpen && !this.desktop.matches && this.contains(document.activeElement)) {
          this.openButton.focus();
        }
      }

      setUpGroups() {
        this.querySelectorAll('.ex-industry-filters__toggle').forEach((toggle) => {
          toggle.addEventListener('click', () => {
            toggle.setAttribute(
              'aria-expanded',
              toggle.getAttribute('aria-expanded') === 'true' ? 'false' : 'true'
            );
          });
        });
      }

      /* ---------- Reading and writing the address ---------- */

      readAddress() {
        const params = new URLSearchParams(window.location.search);
        const chosen = (params.get('industrie') || '').split(',').filter(Boolean);
        this.checkboxes.forEach((box) => {
          box.checked = chosen.includes(box.value);
        });

        if (this.searchInput && params.get('q')) this.searchInput.value = params.get('q');

        if (this.sortSelect && params.get('tri')) {
          const wanted = params.get('tri');
          if (Array.from(this.sortSelect.options).some((o) => o.value === wanted)) {
            this.sortSelect.value = wanted;
          }
        }
        this.updateFilterCount();
      }

      writeAddress() {
        const params = new URLSearchParams(window.location.search);
        const chosen = this.checkboxes.filter((b) => b.checked).map((b) => b.value);

        if (chosen.length) params.set('industrie', chosen.join(','));
        else params.delete('industrie');

        const query = this.searchInput ? this.searchInput.value.trim() : '';
        if (query) params.set('q', query);
        else params.delete('q');

        const sort = this.sortSelect ? this.sortSelect.value : '';
        if (sort && sort !== 'featured') params.set('tri', sort);
        else params.delete('tri');

        const search = params.toString();
        window.history.replaceState(
          {},
          '',
          window.location.pathname + (search ? '?' + search : '')
        );
      }

      /* ---------- Showing, ordering and counting ---------- */

      // Accent-insensitive, so "hotellerie" finds "Hôtellerie"
      static fold(text) {
        return (text || '')
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .trim();
      }

      apply(options) {
        const chosen = this.checkboxes.filter((b) => b.checked).map((b) => b.value);
        const query = ExIndustryListing.fold(this.searchInput ? this.searchInput.value : '');

        let shown = 0;
        this.items.forEach((item) => {
          const title = item.dataset.title || '';
          const byFilter = !chosen.length || chosen.indexOf(title) !== -1;
          const haystack = ExIndustryListing.fold(title + ' ' + (item.dataset.text || ''));
          const bySearch = !query || haystack.indexOf(query) !== -1;
          const visible = byFilter && bySearch;
          item.hidden = !visible;
          if (visible) shown += 1;
        });

        this.sortItems();
        this.updateCount(shown);
        if (this.emptyEl) this.emptyEl.setAttribute('data-empty-shown', shown === 0 ? 'true' : 'false');
        if (!options || options.push !== false) this.writeAddress();
      }

      sortItems() {
        const key = this.sortSelect ? this.sortSelect.value : 'featured';
        const ordered = this.items.slice();

        if (key === 'a-z' || key === 'z-a') {
          ordered.sort((a, b) => (a.dataset.title || '').localeCompare(b.dataset.title || '', 'fr'));
          if (key === 'z-a') ordered.reverse();
        } else {
          ordered.sort((a, b) => Number(a.dataset.order) - Number(b.dataset.order));
        }

        ordered.forEach((item) => this.grid.appendChild(item));
      }

      updateCount(shown) {
        if (!this.countEl) return;
        const one = this.countEl.dataset.one;
        const many = this.countEl.dataset.many || '[count]';
        // A separate wording for one, so it never reads "1 industries"
        this.countEl.textContent = shown === 1 && one ? one : many.replace('[count]', shown);
      }

      updateFilterCount() {
        if (!this.openButton) return;
        const label = this.openButton.querySelector('span:first-child');
        if (!label) return;

        let count = this.openButton.querySelector('.ex-industry-filters__count');
        const on = this.checkboxes.filter((b) => b.checked).length;

        if (!on) {
          if (count) count.remove();
          return;
        }
        if (!count) {
          count = document.createElement('span');
          count.className = 'ex-industry-filters__count';
          label.appendChild(count);
        }
        count.textContent = on;
      }

      /* ---------- Events ---------- */

      bind() {
        this.checkboxes.forEach((box) =>
          box.addEventListener('change', () => {
            this.updateFilterCount();
            this.apply();
          })
        );

        if (this.sortSelect) this.sortSelect.addEventListener('change', () => this.apply());

        if (this.searchInput) {
          let timer;
          this.searchInput.addEventListener('input', () => {
            window.clearTimeout(timer);
            timer = window.setTimeout(() => this.apply(), 200);
          });
        }

        const reset = this.querySelector('[data-ex-industry-reset]');
        if (reset) {
          reset.addEventListener('click', () => {
            this.checkboxes.forEach((b) => {
              b.checked = false;
            });
            if (this.searchInput) this.searchInput.value = '';
            this.updateFilterCount();
            this.apply();
          });
        }
      }
    }
  );
}
